import { useState, useEffect, useRef } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/utils/formatters";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { UploadSimple, MagnifyingGlass, FileXls, Users, Trash, Warning } from "@phosphor-icons/react";

export function GuestRegistryTab() {
  const [guests, setGuests] = useState([]);
  const [total, setTotal] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [confirmClean, setConfirmClean] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const fileRef = useRef(null);

  const loadGuests = async (roomFilter = "") => {
    try {
      const params = roomFilter ? `?room=${roomFilter}` : "";
      const { data } = await api.get(`/guest-registry${params}`);
      setGuests(data.guests);
      setTotal(data.total);
    } catch (err) {
      console.error("Failed to load guests:", err);
    }
  };

  useEffect(() => { loadGuests(); }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".xml")) {
      toast.error("Please upload an XML file");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post("/guest-registry/import", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(data.message);
      loadGuests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleSearch = () => {
    loadGuests(search.trim());
  };

  const handleClearSearch = () => {
    setSearch("");
    loadGuests();
  };

  const handleClean = async (type) => {
    setCleaning(true);
    try {
      const { data } = await api.delete(`/guest-registry/${type}`);
      toast.success(data.message);
      setConfirmClean(null);
      loadGuests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Delete failed");
    } finally {
      setCleaning(false);
    }
  };

  return (
    <div className="space-y-6 max-w-5xl">
      {/* Upload Card */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <FileXls size={16} weight="bold" className="text-[#007AFF]" />
            Import Guest Registry (XML)
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#737373]">Upload a GIBYROOM XML file to replace the current guest registry. The system will extract room numbers, guest names, check-in and check-out dates.</p>
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept=".xml" className="hidden" onChange={handleUpload} data-testid="guest-xml-file" />
            <Button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="bg-[#007AFF] hover:bg-[#3395FF] text-white rounded-sm text-xs" data-testid="guest-import-btn">
              <UploadSimple size={16} className="mr-1.5" />
              {uploading ? "Importing..." : "Upload XML File"}
            </Button>
            <span className="text-xs text-[#737373]">{total} guest records loaded</span>
          </div>
        </CardContent>
      </Card>

      {/* Delete Guest Records */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
            <Trash size={16} weight="bold" className="text-[#FF3B30]" />
            Delete Guest Records
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-[#737373]">Remove guest registry data. This action is logged in the audit trail.</p>
          <div className="flex items-center gap-3">
            <Button size="sm" onClick={() => setConfirmClean("today")} disabled={cleaning || total === 0}
              className="bg-[#FF9F0A] hover:bg-[#FF9F0A]/80 text-black rounded-sm text-xs" data-testid="guest-today-clean-btn">
              <Trash size={14} className="mr-1" /> Today Clean
            </Button>
            <Button size="sm" onClick={() => setConfirmClean("full")} disabled={cleaning || total === 0}
              className="bg-[#FF3B30] hover:bg-[#FF3B30]/80 text-white rounded-sm text-xs" data-testid="guest-full-clean-btn">
              <Trash size={14} className="mr-1" /> Full Clean
            </Button>
          </div>
          <div className="text-xs text-[#737373] space-y-1">
            <p><span className="text-[#FF9F0A] font-semibold">Today Clean</span> — Deletes only the latest XML import data</p>
            <p><span className="text-[#FF3B30] font-semibold">Full Clean</span> — Deletes all guest records from start until now</p>
          </div>
          {confirmClean && (
            <div className={`${confirmClean === "full" ? "bg-[#FF3B30]/10 border-[#FF3B30]/30" : "bg-[#FF9F0A]/10 border-[#FF9F0A]/30"} border rounded-sm p-4 space-y-3`}>
              <p className="text-sm font-semibold" style={{ color: confirmClean === "full" ? "#FF3B30" : "#FF9F0A" }}>
                <Warning size={14} weight="bold" className="inline mr-1" />
                {confirmClean === "full"
                  ? `Are you sure? This will delete ALL ${total} guest records permanently.`
                  : "Are you sure? This will delete the latest XML import data."}
              </p>
              <div className="flex items-center gap-2">
                <Button size="sm" onClick={() => handleClean(confirmClean)} disabled={cleaning}
                  className="text-white rounded-sm text-xs"
                  style={{ backgroundColor: confirmClean === "full" ? "#FF3B30" : "#FF9F0A" }}
                  data-testid="guest-clean-confirm-btn">
                  {cleaning ? "Deleting..." : "Confirm Delete"}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setConfirmClean(null)}
                  className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm text-xs">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Search + Guest List */}
      <Card className="bg-[#121212] border-[#262626] rounded-sm shadow-none">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-sm font-semibold text-white uppercase tracking-wider flex items-center gap-2">
              <Users size={16} weight="bold" className="text-[#007AFF]" />
              Guest Registry
            </CardTitle>
            <div className="flex items-center gap-2">
              <Input value={search} onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleSearch(); }}
                placeholder="Search by room..."
                className="bg-[#0A0A0A] border-[#262626] text-white rounded-sm w-40 h-8 text-xs" data-testid="guest-search-input" />
              <Button size="sm" variant="outline" onClick={handleSearch}
                className="border-[#262626] text-[#A3A3A3] hover:bg-[#1A1A1A] hover:text-white rounded-sm h-8 px-2" data-testid="guest-search-btn">
                <MagnifyingGlass size={14} />
              </Button>
              {search && (
                <button onClick={handleClearSearch} className="text-xs text-[#007AFF] hover:underline" data-testid="guest-clear-search">Clear</button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-auto max-h-[60vh]">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#262626] hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Room</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Tags</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Guest Name</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Check-In</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.15em] text-[#737373]">Check-Out</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {guests.length === 0 ? (
                  <TableRow className="hover:bg-transparent">
                    <TableCell colSpan={5} className="text-center py-12 text-[#737373] text-sm">
                      {total === 0 ? "No guest registry loaded. Upload an XML file to get started." : "No guests found for this room."}
                    </TableCell>
                  </TableRow>
                ) : (
                  guests.map((g, i) => (
                    <TableRow key={`${g.room}-${i}`} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A]">
                      <TableCell className="font-mono-data text-sm text-[#007AFF] font-semibold">{g.room}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 flex-wrap">
                          {g.vip && (
                            <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider bg-[#FFD60A]/20 text-[#FFD60A] border border-[#FFD60A]/40">{g.vip}</span>
                          )}
                          {!g.vip && g.is_long_stay && (
                            <span className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold uppercase tracking-wider bg-[#BF5AF2]/20 text-[#BF5AF2] border border-[#BF5AF2]/40">{g.rate_code}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-white">{g.guest_name}</TableCell>
                      <TableCell className="font-mono-data text-sm text-[#32D74B]">{g.arrival}</TableCell>
                      <TableCell className="font-mono-data text-sm text-[#FF9F0A]">{g.departure}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
